import { DEFAULT_MESSAGE } from "components/UnavailableMessageNotice";
import {
	FaChevronDown,
	FaChevronUp,
	FaGlobe,
	FaPaypal,
	FaStar,
	FaTicketAlt,
	FaTimes,
} from "react-icons/fa";

const renderIcon = (iconName, className = "") => {
	switch (iconName) {
		case "ecocash":
			return <FaGlobe size={22} className={`text-green-600 ${className}`} />;
		case "paypal":
			return <FaPaypal size={22} className={`text-akgreen ${className}`} />;
		case "stars":
			return <FaStar size={22} className={`text-yellow-500 ${className}`} />;
		case "voucher":
			return <FaTicketAlt size={22} className={`text-purple-600 ${className}`} />;
		default:
			return null;
	}
};

const UnavailableIcon = ({ className = "" }) => (
	<span
		className={`inline-flex items-center justify-center rounded-full bg-amber-100 text-red-600 ${className}`}
		aria-label="Unavailable"
		title="Unavailable"
	>
		<FaTimes size={15} />
	</span>
);

const PaymentMethodPills = ({ options, selectedMethod, onSelect }) => {
	return (
		<div>
			<p className="text-xs font-medium text-gray-600 mb-1.5">Payment method</p>
			<div className="flex flex-wrap gap-1.5">
				{options.map((option) => {
					const isSelected = selectedMethod === option.paymentMethod;
					const isDisabled = !option.enabled;
					const unavailableMessage = option.disabledMessage || DEFAULT_MESSAGE;

					return (
						<button
							key={option.paymentMethod}
							type="button"
							aria-pressed={isSelected}
							title={isDisabled ? unavailableMessage : undefined}
							onClick={() => onSelect(option.paymentMethod)}
							className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
								isSelected
									? isDisabled
										? "border-amber-400 bg-amber-50 text-amber-900"
										: "border-akgreen bg-green-50 text-akgreen"
									: isDisabled
										? "border-amber-200 bg-white text-gray-500 hover:border-amber-300"
										: "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
							}`}
						>
							<span className="w-4 h-4 flex items-center justify-center overflow-hidden">
								{renderIcon(option.icon, isDisabled && !isSelected ? "opacity-60" : "")}
							</span>
							<span>{option.label}</span>
							{isDisabled && <UnavailableIcon className="w-4 h-4 flex-shrink-0" />}
						</button>
					);
				})}
			</div>
		</div>
	);
};

const PaymentMethodCards = ({ options, selectedMethod, onSelect }) => {
	return (
		<div className="space-y-3">
			{options.map((option) => {
				const isSelected = selectedMethod === option.paymentMethod;
				const isDisabled = !option.enabled;
				const unavailableMessage = option.disabledMessage || DEFAULT_MESSAGE;

				return (
					<div
						key={option.paymentMethod}
						className={`border rounded-xl overflow-hidden transition-all duration-300 ${
							isSelected
								? isDisabled
									? "border-amber-400 shadow-md"
									: "border-akgreen shadow-md"
								: isDisabled
									? "border-amber-200"
									: "border-gray-200"
						}`}
					>
						<button
							type="button"
							aria-pressed={isSelected}
							title={isDisabled ? unavailableMessage : undefined}
							className={`w-full flex items-center justify-between p-4 ${
								isSelected
									? isDisabled
										? "bg-amber-50"
										: "bg-akgreen bg-opacity-10"
									: isDisabled
										? "bg-amber-50/50 hover:bg-amber-50"
										: "bg-gray-50 hover:bg-gray-100"
							}`}
							onClick={() => onSelect(isSelected ? null : option.paymentMethod)}
						>
							<div className="flex items-center gap-3 flex-1">
								<span className="text-xl">
									{renderIcon(option.icon, isDisabled && !isSelected ? "opacity-60" : "opacity-80")}
								</span>
								<div className="flex flex-col items-start text-left">
									<span className="font-semibold flex items-center gap-2 text-gray-800">
										{option.label}
										{isDisabled && <UnavailableIcon className="w-5 h-5 flex-shrink-0" />}
									</span>
									<span className="text-xs text-gray-500">{option.processingTime}</span>
								</div>
							</div>
							{isSelected ? (
								<FaChevronUp
									className={`flex-shrink-0 ${isDisabled ? "text-amber-600" : "text-akgreen"}`}
								/>
							) : (
								<FaChevronDown className="text-gray-400 flex-shrink-0" />
							)}
						</button>
					</div>
				);
			})}
		</div>
	);
};

const PaymentMethodSelector = ({
	options = [],
	selectedMethod,
	onSelect,
	variant = "pills",
	label,
}) => {
	if (!options.length) {
		return (
			<div className="text-center py-6 text-gray-500 text-sm">
				<p>
					No payment methods are currently available. Please try again later or contact support.
				</p>
			</div>
		);
	}

	return (
		<div>
			{label && <p className="text-xs font-medium text-gray-600 mb-1.5">{label}</p>}
			{variant === "cards" ? (
				<PaymentMethodCards options={options} selectedMethod={selectedMethod} onSelect={onSelect} />
			) : (
				<PaymentMethodPills options={options} selectedMethod={selectedMethod} onSelect={onSelect} />
			)}
		</div>
	);
};

export default PaymentMethodSelector;
